<!-- Note Drawer - Component-Based Architecture -->
<div class="note-drawer" [formGroup]="noteForm" [class.note-drawer--fullscreen]="isFullscreen">
  <div class="note-drawer-layout">
    <!-- LEFT: Sidebar -->
    <app-note-sidebar [noteForm]="noteForm" [noteId]="noteId" [mode]="mode" [createdOn]="model?.created_at"
      [createdBy]="model?.created_by?.full_name" [moduleLabel]="getModuleLabel()" [projectName]="getProjectName()"
      [statuses]="statuses" [selectedStatus]="selectedStatus" [modules]="modulesList"
      [showModuleControls]="isNoteModule" (fieldChange)="handleFieldChange($event)"
      (statusChange)="handleStatusChange($event)" (moduleSelected)="handleModuleSelected($event)"
      (referenceSelected)="handleReferenceSelected($event)" (selfAssignChange)="handleSelfAssignChange($event)"
      (onCollapse)="handleSidebarCollapse($event)">
    </app-note-sidebar>

    <!-- RIGHT: Main Area -->
    <div class="note-main-area">
      <!-- Header -->
      <app-note-header [noteId]="noteId" [noteName]="noteForm.get('name')?.value" [mode]="mode" [flagged]="CONST_FLAG"
        [moduleLabel]="getModuleLabel()" [projectName]="getProjectName()" [projectId]="getRecordNumber()"
        [recordRoute]="getRecordRoute()" [status]="noteForm.get('status')?.value"
        [priority]="noteForm.get('priority')?.value" (flagToggle)="handleFlagToggle($event)"
        (searchToggle)="handleSearchToggle()" (fullscreenToggle)="handleFullscreenToggle()"
        (deleteNote)="handleDeleteNote()" (closeDrawer)="close()" (recordLinkClick)="close()" (priorityToggle)="handlePriorityToggle()"
        (markComplete)="handleMarkComplete()" (exportPDF)="handleExportPDF()" (editNote)="handleEditNote()"
        (newNote)="handleNewNote()">
      </app-note-header>

      <!-- Main Content -->
      <app-note-main-content [mode]="mode" [noteForm]="noteForm" [activeTab]="activeTabIndex"
        [hideDetailsCommentsHistory]="hideDetailsCommentsHistory" [attachmentsCount]="attachmentsCount"
        [commentsCount]="commentsCount" [historyCount]="historyCount" [mentions]="mentions" [isMentionsLoading]="isMentionsLoading"
        (tabChange)="handleTabChange($event)" (fieldChange)="handleFieldChange($event)"
        (editDescription)="handleEditDescription()" (mentionSelected)="getSelectedUser($event)"
        (mentionSearch)="getUsers($event)">

        <!-- Module & Reference Selection (for isNoteModule) -->
        @if (isNoteModule && (mode === 'create' || mode === 'edit')) {
          <div class="note-module-reference-section">
            <div class="module-reference-grid">
              <!-- Module Select -->
              <div class="form-field">
                <label class="field-label">Module</label>
                <ng-select id="scope" [items]="modulesList" placeholder="Select Module" [closeOnSelect]="true"
                  bindLabel="name" (change)="selectedModule($event)" appendTo="body">
                </ng-select>
              </div>
              <!-- Reference Search -->
              <div class="form-field">
                <label class="field-label">Reference</label>
                @if (!showSearchInput) {
                  <div class="reference-selected">
                    <span class="reference-text">{{ this.selectedItem?.data ?? 'Search' }}</span>
                    <button class="reference-clear-btn" (click)="updateSearchInputFlag()" type="button">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
                @if (showSearchInput) {
                  <app-search-field [searchAbleDataService]="taskDataService"
                    [searchAbleFunctionName]="'searchTaskReferenceNumber'" [searchAbleParams]="searchParams"
                    [labelKey]="'data'" [searchPlaceHolder]="'Search reference number'"
                    (onSelectValue)="onItemSelect($event)">
                  </app-search-field>
                }
              </div>
            </div>
          </div>
        }

        <!-- Details Tab Content -->
        <div details-tab>
          @if (model && (mode === 'view' || mode === 'edit')) {
            <app-note-details-tab [noteData]="model"
              [displayMode]="mode === 'edit' ? 'summary' : 'full'" [statuses]="statuses"
              [statusId]="noteForm.get('status')?.value" [priorityValue]="noteForm.get('priority')?.value">
            </app-note-details-tab>
          }
        </div>

        <!-- Attachments Tab Content -->
        <div attachments-tab>
          <!-- CREATE MODE: Use shared attachment component (no note ID yet) -->
          @if (!noteId) {
            <app-attachment
              [dropZoneId]="'noteAttachment'"
              [module_id]="NOTE_MODULE_ID"
              [multipleUpload]="true"
              [allowAllFileTypes]="true"
              (callback)="getFileID($event)"
              (onDelete)="handleAttachmentDelete($event)">
            </app-attachment>
          }

          <!-- EDIT MODE: Use note-attachments component (note exists) -->
          @if (noteId) {
            <app-note-attachments
              [module_id]="NOTE_MODULE_ID"
              [attachable_id]="noteId.toString()"
              (attachmentsCountChanged)="handleAttachmentCountChange($event)">
            </app-note-attachments>
          }
        </div>

        <!-- Comments Tab Content (Edit Mode Only) -->
        <div comments-tab>
          @if (mode === 'edit' && noteId) {
            <app-note-comment [model]="model" [noteID]="noteId"
              [isActiveTab]="activeTabIndex === 2" (commentCountChanged)="handleCommentCountChange($event)">
            </app-note-comment>
          }
        </div>

        <!-- History Tab Content -->
        <div history-tab>
          @if (mode === 'view' || mode === 'edit') {
            <app-note-history-tab [noteId]="noteId" [noteModel]="model"
              (historyChange)="handleHistoryCountChange($event)">
            </app-note-history-tab>
          }
        </div>
      </app-note-main-content>
    </div>
  </div>

  <!-- Footer -->
  <div class="note-footer">
    <div class="footer-left">
      <!-- Save as Template (Create Mode Only) -->
      @if (!noteId) {
        <div class="save-template-wrapper">
          <mat-slide-toggle formControlName="is_template" class="save-template-toggle b-one-switch">
            Save as Template
          </mat-slide-toggle>
        </div>
      }
    </div>

    <div class="footer-right">
      <!-- Cancel Button -->
      <button type="button" mat-stroked-button class="footer-cancel-btn" (click)="close()" [attr.aria-label]="'Cancel'">
        <span class="btn-text">CANCEL</span>
      </button>

      <!-- Save Button with Spinner -->
      <button type="button" mat-raised-button color="primary" class="footer-save-btn" (click)="save()"
        [disabled]="isSaving" [attr.aria-label]="noteId ? 'Update note' : 'Save note'">
        <span class="btn-content" [class.btn-content--loading]="isSaving">
          @if (!isSaving) {
            <mat-icon>save</mat-icon>
          }
          @if (isSaving) {
            <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
          }
          <span class="btn-text">{{ noteId ? 'UPDATE NOTE' : 'ADD NOTE' }}</span>
        </span>
      </button>
    </div>
  </div>
</div>